import type { CameraPickerPreset } from "@/components/cameras/camera-picker-dialog"
import { handleJson, jsonHeaders } from "@/lib/json-request"

export type CameraPresetWritePayload = {
  name?: string
  description?: string | null
  narrative_purpose?: string | null
  when_to_apply?: string | null
  system_prompt_fragment?: string | null
  tags?: string | null
  favorite?: boolean
  builtin?: boolean
  hidden?: boolean
}

export type CameraCopyTarget = "project" | "company"

/**
 * Base path for the camera presets endpoints of the current context:
 *  - project: `/projects/:id/workspace/camera_presets`
 *  - studio:  `/studio/cameras`
 *  - company: `/company/cameras`
 */
export function createCameraPreset(basePath: string, payload: CameraPresetWritePayload) {
  return fetch(basePath, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ camera_preset: payload }),
  }).then((res) => handleJson<{ ok: boolean; preset_id: number; message?: string }>(res))
}

export function updateCameraPreset(basePath: string, id: number, payload: CameraPresetWritePayload) {
  return fetch(`${basePath}/${id}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ camera_preset: payload }),
  }).then((res) => handleJson<{ ok: boolean; preset_id: number; message?: string }>(res))
}

export function deleteCameraPreset(basePath: string, id: number) {
  return fetch(`${basePath}/${id}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  }).then((res) => handleJson<{ ok: boolean; message?: string }>(res))
}

export function generateCameraDemo(basePath: string, id: number, prompt: string) {
  return fetch(`${basePath}/${id}/generate_demo`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ prompt }),
  }).then((res) => handleJson<{ ok: boolean; preset_id: number; message?: string }>(res))
}

export function copyCameraPreset(basePath: string, id: number, target: CameraCopyTarget) {
  return fetch(`${basePath}/${id}/copy`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ target }),
  }).then((res) => handleJson<{ ok: boolean; preset_id: number; message?: string }>(res))
}

type AvailablePresetsResponse = {
  presets: CameraPickerPreset[]
}

const availablePresetsCache = new Map<number, CameraPickerPreset[]>()
const availablePresetsInflight = new Map<number, Promise<CameraPickerPreset[]>>()

function toPickerPreset(row: CameraPickerPreset): CameraPickerPreset {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? null,
    tags: row.tags ?? [],
    level: row.level,
    demo_video_url: row.demo_video_url ?? null,
    demo_poster_url: row.demo_poster_url ?? null,
  }
}

/** Cached list from a prior picker open in this tab (null if never loaded). */
export function getCachedAvailableCameraPresets(projectId: number): CameraPickerPreset[] | null {
  return availablePresetsCache.get(projectId) ?? null
}

/** Lazy picker list: one network request per projectId, shared across toolbar + segment dialogs. */
export function fetchAvailableCameraPresets(projectId: number): Promise<CameraPickerPreset[]> {
  const cached = availablePresetsCache.get(projectId)
  if (cached) return Promise.resolve(cached)

  const pending = availablePresetsInflight.get(projectId)
  if (pending) return pending

  const request = fetch(`/projects/${projectId}/workspace/camera_presets/available`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
    .then((res) => handleJson<AvailablePresetsResponse>(res))
    .then((data) => {
      const presets = (data.presets ?? []).map(toPickerPreset)
      availablePresetsCache.set(projectId, presets)
      availablePresetsInflight.delete(projectId)
      return presets
    })
    .catch((error: unknown) => {
      availablePresetsInflight.delete(projectId)
      throw error
    })

  availablePresetsInflight.set(projectId, request)
  return request
}
